import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export const Stars = ({ n, size = 15, testId }) => (
  <span className="stars" aria-label={`${n} out of 5 stars`} data-testid={testId}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={size} fill={i <= Math.round(n) ? "currentColor" : "none"} />)}
  </span>
);

export const useReviews = () => useQuery({ queryKey: ["reviews"], queryFn: async () => (await api.get("/reviews")).data, staleTime: 60_000 });

const GoogleBadge = () => <span className="g-badge" aria-label="Google"><b style={{ color: "#4285F4" }}>G</b>oogle</span>;

export const GoogleReviews = ({ g, url }) => (
  <div className="rv-google" data-testid="google-reviews">
    <div className="rv-google-head">
      <div>
        <GoogleBadge /> <span className="muted-sm">reviews</span>
        {g && <p className="rv-score" data-testid="google-rating"><strong>{g.rating?.toFixed(1)}</strong> <Stars n={g.rating || 0} size={17} /> <span>{g.count} reviews</span></p>}
        {!g && <p className="muted-sm">See what clients are saying about Wax Moi on Google.</p>}
      </div>
      <div className="rv-google-btns">
        {g?.maps_url && <a className="btn light" href={g.maps_url} target="_blank" rel="noreferrer" data-testid="google-see-all-link">See all on Google <ExternalLink size={13} /></a>}
        <a className="btn gold" href={url} target="_blank" rel="noreferrer" data-testid="google-leave-review-link">Leave a Google review <ExternalLink size={13} /></a>
      </div>
    </div>
    {g?.reviews?.length > 0 && (
      <div className="rv-grid">
        {g.reviews.map((r, i) => (
          <article className="rv-card" key={`${r.author}-${r.when}-${i}`} data-testid={`google-review-${i}`}>
            <header>
              {r.photo ? <img src={r.photo} alt="" width="36" height="36" referrerPolicy="no-referrer" /> : <span className="rv-av">{r.author[0]}</span>}
              <div>{r.author_url ? <a href={r.author_url} target="_blank" rel="noreferrer">{r.author}</a> : <b>{r.author}</b>}<small>{r.when}</small></div>
            </header>
            <Stars n={r.rating} />
            <p>{r.text}</p>
            {r.url && <a className="rv-src" href={r.url} target="_blank" rel="noreferrer">Posted on Google</a>}
          </article>
        ))}
      </div>
    )}
  </div>
);

export const SiteReviews = ({ list, avg, count }) => (
  <div className="rv-site" data-testid="site-reviews">
    <h3>Reviews from our website {count > 0 && <span className="rv-score"><strong>{avg}</strong> <Stars n={avg} /> <span>{count}</span></span>}</h3>
    {!list.length ? <p className="muted-sm" data-testid="site-reviews-empty">Be the first to leave a review below.</p> : (
      <div className="rv-grid">
        {list.map((r) => (
          <article className="rv-card" key={r.id} data-testid={`site-review-${r.id}`}>
            <header><span className="rv-av">{r.name[0]}</span><div><b>{r.name}</b><small>{new Date(r.created_at).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</small></div></header>
            <Stars n={r.rating} />
            <p>{r.text}</p>
          </article>
        ))}
      </div>
    )}
  </div>
);

const EMPTY = { name: "", email: "", rating: 0, text: "", website: "" };

export const ReviewForm = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.rating) { toast.error("Please choose a star rating"); return; }
    setBusy(true);
    try { await api.post("/reviews", f); setDone(true); setF(EMPTY); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  if (done) return <div className="rv-form rv-thanks" data-testid="review-thanks"><h3>Thank you!</h3><p>Your review was sent and will appear here once our team approves it.</p><button className="btn light" onClick={() => setDone(false)} data-testid="review-write-another-button">Write another</button></div>;
  return (
    <form className="rv-form" onSubmit={submit} data-testid="review-form">
      <h3>Leave a review</h3>
      <div className="rv-pick" role="radiogroup" aria-label="Your rating">
        {[1, 2, 3, 4, 5].map((i) => (
          <button type="button" key={i} role="radio" aria-checked={f.rating === i} aria-label={`${i} star${i > 1 ? "s" : ""}`} onClick={() => setF({ ...f, rating: i })} data-testid={`review-star-${i}`}>
            <Star size={28} fill={i <= f.rating ? "currentColor" : "none"} />
          </button>
        ))}
      </div>
      <input required maxLength={60} placeholder="Your name" value={f.name} onChange={set("name")} data-testid="review-name-input" />
      <input type="email" maxLength={120} placeholder="Email (optional, never shown)" value={f.email} onChange={set("email")} data-testid="review-email-input" />
      <textarea required minLength={5} maxLength={1200} rows={4} placeholder="Tell us about your visit" value={f.text} onChange={set("text")} data-testid="review-text-input" />
      <input className="hp" tabIndex={-1} autoComplete="off" aria-hidden="true" value={f.website} onChange={set("website")} />
      <button className="btn gold" type="submit" disabled={busy} data-testid="review-submit-button">{busy ? <Loader2 size={16} className="spin" /> : "Submit review"}</button>
    </form>
  );
};
